import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { motion } from 'framer-motion';
import RecommendationsWidget from '../components/RecommendationsWidget';
import { format, addDays, startOfWeek, isSameDay, getDay, startOfDay, isAfter } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { 
  CaretLeft, 
  CaretRight, 
  Clock,
  CalendarBlank,
  CreditCard,
  Check,
  Sun,
  Moon,
  Bell,
  Info,
  X
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BookingCalendar = () => {
  const { user, t, language, API } = useAuth();
  const dateLocale = language === 'de' ? de : enUS;

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [courts, setCourts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingDuration, setBookingDuration] = useState('60');
  // Customer flow: pick DURATION FIRST, then the time-pill grid only shows
  // starting times where at least one court can actually host that duration
  // (no overlap, no orphan <60min gaps). This mirrors the Open-Matches flow
  // and stops customers from tapping a "free" slot only to find no legal
  // duration option inside the dialog.
  const [filterDuration, setFilterDuration] = useState(60);
  const [paymentMethod, setPaymentMethod] = useState('cash'); // cash (default: Pay at Club), online (Datatrans), credits
  const [payScope, setPayScope] = useState('all'); // 'self' | 'all' — split-payment: pay own share or the whole court (only relevant for online payment)
  const [memberDiscount, setMemberDiscount] = useState(0);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [courtType, setCourtType] = useState('padel'); // 'padel' | 'golf'
  const [myWaitlist, setMyWaitlist] = useState([]);
  const [myGolfYearlyAbo, setMyGolfYearlyAbo] = useState(null);
  // Time → court picker: null = closed. When a customer taps a time pill we
  // stash the time here so the picker dialog can list only the courts that
  // are actually free at that moment.
  const [timePickerFor, setTimePickerFor] = useState(null);

  const loadMyWaitlist = async () => {
    try {
      const { data } = await API.get('/waitlist/mine');
      setMyWaitlist(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading waitlist:', error);
    }
  };

  // Refresh the customer's active Golf yearly abo (if any) so the booking
  // modal can offer "Golf Jahres-Abo" as a free payment method for Golf slots.
  const loadMyGolfYearlyAbo = async () => {
    try {
      const { data } = await API.get('/subscriptions/my');
      const now = new Date();
      const active = (data || []).find((s) =>
        s.is_yearly &&
        s.status === 'active' &&
        s.valid_until &&
        new Date(s.valid_until) > now
      );
      setMyGolfYearlyAbo(active || null);
    } catch (error) {
      setMyGolfYearlyAbo(null);
    }
  };

  useEffect(() => {
    loadMyWaitlist();
    loadMyGolfYearlyAbo();
  }, []);

  // Quick lookup: am I on the waitlist for this date+time+court_type?
  const isOnWaitlist = (date, time) => {
    const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
    return myWaitlist.some(w =>
      w.date === dateStr && w.start_time === time && w.court_type === courtType,
    );
  };

  const handleJoinWaitlist = async (time) => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await API.post('/waitlist', {
        date: dateStr,
        start_time: time,
        court_type: courtType,
      });
      toast.success(language === 'de'
        ? 'Du stehst jetzt auf der Warteliste. Wir mailen dich an, sobald ein Platz frei wird.'
        : "You're on the waitlist. We'll email you the moment a court frees up.");
      loadMyWaitlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'de' ? 'Konnte nicht auf die Warteliste setzen.' : 'Could not join the waitlist.'));
    }
  };

  const handleLeaveWaitlist = async (entryId) => {
    try {
      await API.delete(`/waitlist/${entryId}`);
      toast.success(language === 'de' ? 'Von der Warteliste entfernt.' : 'Removed from waitlist.');
      loadMyWaitlist();
    } catch {
      toast.error(language === 'de' ? 'Fehler beim Entfernen.' : 'Failed to remove.');
    }
  };

  // Generate time slots based on opening hours
  const getTimeSlots = () => {
    if (!pricingInfo) return [];
    const openHour = parseInt(pricingInfo.opening_time.split(':')[0]);
    const closeHour = pricingInfo.closing_time === '00:00' ? 24 : parseInt(pricingInfo.closing_time.split(':')[0]);
    
    const slots = [];
    for (let h = openHour; h < closeHour; h++) {
      for (let m = 0; m < 60; m += 30) {
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }
    return slots;
  };

  const timeSlots = getTimeSlots();

  /**
   * A court+time is bookable for the customer only when the *requested*
   * duration fits without an orphan <60min gap. When `duration` is null we
   * fall back to the historical behaviour (any of 60/90/120 fits) — used by
   * the admin/legacy paths that don't pre-filter by duration.
   *
   * Mirrors the backend's `check_orphan_gap` guard.
   */
  const toMin = (t) => parseInt(t.split(':')[0], 10) * 60 + parseInt(t.split(':')[1], 10);

  // Durations (60/90/120) playable from this start without an orphan <60min gap
  // before or after — mirrors backend `check_orphan_gap` exactly.
  const fittingDurations = (courtId, time) => {
    if (!time || !courtId) return [];
    const startMin = toMin(time);
    const closeStr = pricingInfo?.closing_time || '24:00';
    const closingMin = closeStr === '00:00' ? 24 * 60 : toMin(closeStr);
    const openingMin = pricingInfo?.opening_time ? toMin(pricingInfo.opening_time) : null;
    let nearestAfterStart = null;
    let nearestBeforeEnd = null;
    for (const b of bookings) {
      if (!b || b.status === 'cancelled' || b.court_id !== courtId || !b.start_time) continue;
      try {
        const bs = toMin(b.start_time);
        const be = b.end_time ? toMin(b.end_time) : bs + (parseInt(b.duration_minutes, 10) || 60);
        if (be <= startMin) { if (nearestBeforeEnd === null || be > nearestBeforeEnd) nearestBeforeEnd = be; }
        else if (bs >= startMin && (nearestAfterStart === null || bs < nearestAfterStart)) nearestAfterStart = bs;
      } catch (_) { /* skip malformed */ }
    }
    const beforeGap = nearestBeforeEnd !== null ? startMin - nearestBeforeEnd : (openingMin !== null ? startMin - openingMin : 0);
    if (beforeGap > 0 && beforeGap < 60) return [];
    const isSunday = selectedDate && selectedDate.getDay() === 0;
    return [60, 90, 120].filter((d) => {
      const endMin = startMin + d;
      if (endMin > closingMin) return false;
      if (nearestAfterStart !== null && endMin > nearestAfterStart) return false;
      // Sunday: a short gap before closing is OK (e.g. 18:30–19:30)
      if (nearestAfterStart === null && isSunday) return true;
      const gap = (nearestAfterStart !== null ? nearestAfterStart : closingMin) - endMin;
      return !(gap > 0 && gap < 60);
    });
  };

  const hasBookableDuration = (courtId, time, duration = null) => {
    const fits = fittingDurations(courtId, time);
    return duration ? fits.includes(duration) : fits.length > 0;
  };

  useEffect(() => {
    loadCourts();
  }, [courtType]);

  useEffect(() => {
    if (selectedDate) {
      loadBookings(selectedDate);
      loadPricing(selectedDate);
    }
  }, [selectedDate, courtType]);

  const loadCourts = async () => {
    try {
      const { data } = await API.get(`/courts?type=${courtType}`);
      setCourts(data);
    } catch (error) {
      console.error('Error loading courts:', error);
    }
  };

  const loadPricing = async (date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const { data } = await API.get(`/pricing?date=${dateStr}&court_type=${courtType}`);
      setPricingInfo(data);
    } catch (error) {
      console.error('Error loading pricing:', error);
    }
  };

  const loadBookings = async (date) => {
    try {
      setLoading(true);
      const dateStr = format(date, 'yyyy-MM-dd');
      const { data } = await API.get(`/bookings/calendar?date=${dateStr}`);
      setBookings([...data.bookings, ...data.matches]);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const isCourtBlocked = (court) => {
    // Court has been explicitly blocked by an admin for the selected date
    // (see POST /courts/{id}/block). These slots must never be presented as
    // free — the client would only find out at checkout time otherwise.
    if (!court || !Array.isArray(court.blocked_dates)) return false;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    return court.blocked_dates.includes(dateStr);
  };

  const isSlotBooked = (courtId, time) => {
    const court = courts.find(c => c.id === courtId);
    if (isCourtBlocked(court)) return true;
    return bookings.some(b => {
      if (b.court_id !== courtId || b.status === 'cancelled') return false;
      const startMinutes = parseInt(b.start_time.split(':')[0]) * 60 + parseInt(b.start_time.split(':')[1]);
      const endMinutes = parseInt(b.end_time.split(':')[0]) * 60 + parseInt(b.end_time.split(':')[1]);
      const slotMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
      return slotMinutes >= startMinutes && slotMinutes < endMinutes;
    });
  };

  const getSlotBooking = (courtId, time) => {
    return bookings.find(b => {
      if (b.court_id !== courtId || b.status === 'cancelled') return false;
      const startMinutes = parseInt(b.start_time.split(':')[0]) * 60 + parseInt(b.start_time.split(':')[1]);
      const endMinutes = parseInt(b.end_time.split(':')[0]) * 60 + parseInt(b.end_time.split(':')[1]);
      const slotMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
      return slotMinutes >= startMinutes && slotMinutes < endMinutes;
    });
  };

  const handleSlotClick = (court, time) => {
    if (isSlotBooked(court.id, time)) return;
    if (isPastSlot(time)) {
      toast.error(language === 'de'
        ? 'Buchungen in der Vergangenheit sind nicht möglich.'
        : 'Bookings in the past are not allowed.');
      return;
    }
    setSelectedSlot({ court, time, date: selectedDate });
    // Carry the filter-strip duration into the dialog so the user's choice
    // sticks. If it happens to not fit for this specific court/time combo,
    // the dialog's Select will simply hide it and the user can pick another.
    setBookingDuration(String(filterDuration));
    // Auto-select Golf yearly abo as default payment method for eligible customers
    if (courtType === 'golf' && myGolfYearlyAbo) {
      setPaymentMethod('golf_yearly');
      setPayScope('all');
    } else if (paymentMethod === 'golf_yearly') {
      setPaymentMethod('cash');
    }
    setShowBookingModal(true);
  };

  // Returns true when (selectedDate, time) is in the past relative to "now".
  // We use the local timezone for the comparison so the user perceives the
  // same cutoff visually.
  const isPastSlot = (time) => {
    if (!isCustomer) return false;
    const today = new Date();
    today.setSeconds(0, 0);
    const slot = new Date(selectedDate);
    slot.setHours(parseInt(time.split(':')[0], 10), parseInt(time.split(':')[1], 10), 0, 0);
    return slot <= today;
  };

  // Get price for a specific time slot
  const getSlotPrice = (time) => {
    if (!pricingInfo) return 58;
    const hour = parseInt(time.split(':')[0]);
    if (courtType === 'golf') {
      return hour < 17 ? 55 : 65;
    }
    if (pricingInfo.is_weekend) return 68;
    return hour < 17 ? 58 : 68;
  };

  // Full court price (before member discount)
  const calculateFullPrice = () => {
    if (!selectedSlot || !pricingInfo) return 0;
    const startHour = parseInt(selectedSlot.time.split(':')[0]);
    const durationHours = parseInt(bookingDuration) / 60;

    if (courtType === 'golf') {
      if (startHour >= 17) return 65 * durationHours;
      const endHour = startHour + durationHours;
      if (endHour <= 17) return 55 * durationHours;
      const hoursBefore17 = 17 - startHour;
      const hoursAfter17 = durationHours - hoursBefore17;
      return (55 * hoursBefore17) + (65 * hoursAfter17);
    }

    if (pricingInfo.is_weekend) return 68 * durationHours;
    if (startHour >= 17) return 68 * durationHours;

    const endHour = startHour + durationHours;
    if (endHour <= 17) return 58 * durationHours;
    const hoursBefore17 = 17 - startHour;
    const hoursAfter17 = durationHours - hoursBefore17;
    return (58 * hoursBefore17) + (68 * hoursAfter17);
  };

  // Member off-peak 20% discount. Applies only when paying online — for
  // pay-at-club the front-desk applies the discount manually at the till, so
  // the calendar shows the full price and a note instead.
  const calculateMemberDiscount = () => {
    if (courtType !== 'padel') return 0;
    if (!selectedSlot || !pricingInfo || pricingInfo.is_weekend) return 0;
    if (!user?.is_member) return 0;
    // Online-only: pay-at-club bookings keep full price on the confirmation
    // screen and get the discount applied at the till.
    if (paymentMethod !== 'online') return 0;
    const startHour = parseInt(selectedSlot.time.split(':')[0]);
    const startMin = startHour * 60 + parseInt(selectedSlot.time.split(':')[1] || '0');
    const endMin = startMin + parseInt(bookingDuration);
    const boundary = 17 * 60;
    // Full off-peak window is eligible — no 60-minute cap.
    const eligibleMinutes = Math.max(0, Math.min(endMin, boundary) - startMin);
    if (eligibleMinutes <= 0) return 0;
    const NUM_PLAYERS = 4;
    const eligiblePrice = 58 * (eligibleMinutes / 60);
    return Math.round(((eligiblePrice / NUM_PLAYERS) * 0.20) * 100) / 100;
  };

  // Same discount amount but *ignoring* the payment-method gate — used only
  // for the "info banner" that tells pay-at-club members what they'll get off
  // at the till.
  const calculatePotentialMemberDiscount = () => {
    if (courtType !== 'padel') return 0;
    if (!selectedSlot || !pricingInfo || pricingInfo.is_weekend) return 0;
    if (!user?.is_member) return 0;
    const startHour = parseInt(selectedSlot.time.split(':')[0]);
    const startMin = startHour * 60 + parseInt(selectedSlot.time.split(':')[1] || '0');
    const endMin = startMin + parseInt(bookingDuration);
    const boundary = 17 * 60;
    const eligibleMinutes = Math.max(0, Math.min(endMin, boundary) - startMin);
    if (eligibleMinutes <= 0) return 0;
    const NUM_PLAYERS = 4;
    const eligiblePrice = 58 * (eligibleMinutes / 60);
    return Math.round(((eligiblePrice / NUM_PLAYERS) * 0.20) * 100) / 100;
  };

  const calculatePrice = () => {
    // Booker's actual payable amount depending on pay_scope + member discount.
    const full = calculateFullPrice();
    if (courtType === 'golf') return full;                     // no split for golf
    const discount = calculateMemberDiscount();
    if (payScope === 'self') {
      return Math.round(((full / 4) - discount) * 100) / 100;   // 1/4 share − discount
    }
    // pay_scope === 'all'
    return Math.round((full - discount) * 100) / 100;           // whole court, minus their share discount
  };

  const handleBooking = async () => {
    if (!selectedSlot) return;
    
    setBookingLoading(true);
    try {
      const dateStr = format(selectedSlot.date, 'yyyy-MM-dd');
      const isPadel = courtType === 'padel';
      const response = await API.post('/bookings', {
        court_id: selectedSlot.court.id,
        date: dateStr,
        start_time: selectedSlot.time,
        duration_minutes: parseInt(bookingDuration),
        payment_method: paymentMethod,
        use_credits: paymentMethod === 'credits',
        num_players: isPadel ? 4 : 1,
        pay_scope: isPadel ? payScope : 'all',
      });

      if (paymentMethod === 'credits') {
        toast.success(language === 'de' ? 'Buchung bestätigt!' : 'Booking confirmed!');
        setShowBookingModal(false);
        loadBookings(selectedDate);
      } else if (paymentMethod === 'golf_yearly') {
        toast.success(language === 'de' ? 'Buchung bestätigt · Golf Jahres-Abo eingelöst' : 'Booking confirmed · Golf yearly abo used');
        setShowBookingModal(false);
        loadBookings(selectedDate);
      } else if (paymentMethod === 'cash') {
        toast.success(language === 'de' ? 'Buchung bestätigt! Zahlung vor Ort.' : 'Booking confirmed! Pay at the club.');
        setShowBookingModal(false);
        loadBookings(selectedDate);
      } else {
        // Online — for split-payment, only charge the booker's share on checkout.
        let shareId = null;
        if (isPadel && payScope === 'self') {
          try {
            const sr = await API.get(`/bookings/${response.data.id}/shares`);
            const bookerShare = (sr.data.shares || []).find(s => s.is_booker);
            if (bookerShare) shareId = bookerShare.id;
          } catch (e) { /* fall back to full-booking checkout */ }
        }
        const { data } = await API.post('/payments/checkout', {
          booking_id: response.data.id,
          share_id: shareId,
          origin_url: window.location.origin
        });
        window.location.href = data.url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'de' ? 'Buchung fehlgeschlagen' : 'Booking failed');
      toast.error(msg);
    } finally {
      setBookingLoading(false);
    }
  };

  const navigateDate = (direction) => {
    setSelectedDate(prev => {
      const next = addDays(prev, direction);
      if (isCustomer) {
        // Block forward beyond max
        if (isAfter(startOfDay(next), startOfDay(maxBookingDate))) return prev;
        // Block backward past today
        const today = startOfDay(new Date());
        if (next < today) return prev;
      }
      return next;
    });
  };

  const isCustomer = user.role !== 'admin' && user.role !== 'staff';
  const maxBookingDate = isCustomer ? addDays(new Date(), 14) : null;
  const isAtMaxDate = isCustomer && maxBookingDate && !isAfter(startOfDay(maxBookingDate), startOfDay(selectedDate));
  const isAtToday = isCustomer && !isAfter(startOfDay(selectedDate), startOfDay(new Date()));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="booking-calendar">
      {/* AI Recommendations Widget (customers only) */}
      {user?.role === 'customer' && <RecommendationsWidget user={user} />}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
            {t('bookCourt')}
          </h1>
          <p className="text-slate-500">
            {language === 'de' 
              ? 'Wählen Sie einen freien Platz und buchen Sie online'
              : 'Select an available slot and book online'}
          </p>
        </div>
        
        {/* Credits Info */}
        <div className="flex items-center gap-4 bg-white border border-slate-200 px-4 py-3 rounded-lg">
          <CreditCard size={20} className="text-emerald-600" weight="duotone" />
          <div>
            <p className="text-xs text-slate-500 uppercase">{t('credits')}</p>
            <p className="text-lg font-bold text-emerald-600">{user.credits_balance?.toFixed(0)} CHF</p>
          </div>
        </div>
      </div>

      {/* Padel / Golf Tabs */}
      <div className="inline-flex p-1 bg-slate-100 rounded-xl border border-slate-200" data-testid="court-type-tabs">
        <button
          onClick={() => setCourtType('padel')}
          className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${courtType === 'padel' ? 'bg-emerald-500 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
          data-testid="tab-padel"
        >
          🎾 Padel
        </button>
        <button
          onClick={() => setCourtType('golf')}
          className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${courtType === 'golf' ? 'bg-emerald-500 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
          data-testid="tab-golf"
        >
          ⛳ Golf
        </button>
      </div>

      {/* Closure banner — full-width red block when the club is closed for this date */}
      {pricingInfo?.is_closed && (
        <div className="bg-red-50 border-2 border-red-300 p-5 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="closure-banner">
          <div className="text-4xl">🚫</div>
          <div className="flex-1">
            <p className="text-red-800 font-bold text-lg leading-tight">
              {language === 'de' ? 'Center an diesem Tag geschlossen' : 'Club closed on this day'}
            </p>
            <p className="text-red-700 text-sm mt-1">
              {pricingInfo.closure_note || (language === 'de'
                ? 'Bitte wähle einen anderen Tag aus dem Kalender.'
                : 'Please pick another date from the calendar.')}
            </p>
          </div>
        </div>
      )}

      {/* Pricing Info Banner */}
      {pricingInfo && !pricingInfo.is_closed && (
        <div className="bg-white border border-slate-200 p-4 rounded-lg">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-slate-500" />
              <span className="text-slate-500 text-sm">
                {language === 'de' ? 'Öffnungszeiten:' : 'Opening hours:'}
              </span>
              <span className="text-slate-900 font-medium">
                {pricingInfo.opening_time} - {pricingInfo.closing_time === '00:00' ? '24:00' : pricingInfo.closing_time}
              </span>
            </div>
            <div className="flex items-center gap-4">
              {courtType === 'golf' ? (
                <>
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-50 border border-[#34C759]/30 rounded-lg">
                    <Sun size={16} className="text-green-600" />
                    <span className="text-green-600 font-bold">55 CHF/h</span>
                    <span className="text-slate-500 text-xs">(06:00-17:00)</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 border border-[#FF9500]/30 rounded-lg">
                    <Moon size={16} className="text-amber-600" />
                    <span className="text-amber-600 font-bold">65 CHF/h</span>
                    <span className="text-slate-500 text-xs">(17:00-23:00)</span>
                  </div>
                </>
              ) : pricingInfo.is_weekend ? (
                <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 border border-[#FF9500]/30 rounded-lg">
                  <Sun size={16} className="text-amber-600" />
                  <span className="text-amber-600 font-bold">68 CHF/h</span>
                  <span className="text-slate-500 text-xs">({language === 'de' ? 'Wochenende' : 'Weekend'})</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-50 border border-[#34C759]/30 rounded-lg">
                    <Sun size={16} className="text-green-600" />
                    <span className="text-green-600 font-bold">58 CHF/h</span>
                    <span className="text-slate-500 text-xs">(06:00-17:00)</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 border border-[#FF9500]/30 rounded-lg">
                    <Moon size={16} className="text-amber-600" />
                    <span className="text-amber-600 font-bold">68 CHF/h</span>
                    <span className="text-slate-500 text-xs">(17:00-24:00)</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* My Waitlist Entries */}
      {myWaitlist.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="my-waitlist-banner">
          <div className="flex items-center gap-2 mb-2">
            <Bell size={16} weight="fill" className="text-emerald-600" />
            <span className="text-xs font-bold uppercase tracking-wide text-emerald-700">
              {language === 'de' ? 'Deine Wartelisten' : 'Your waitlist'}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {myWaitlist.map(w => (
              <div key={w.id} className="inline-flex items-center gap-2 bg-white border border-emerald-300 rounded-full pl-3 pr-1 py-1 text-xs">
                <span className="font-semibold text-slate-800">
                  {w.court_type === 'golf' ? '⛳' : '🎾'} {w.date} · {w.start_time}
                </span>
                {w.status === 'notified' && (
                  <span className="text-amber-700 font-bold text-[10px] uppercase">
                    {language === 'de' ? 'Platz angeboten!' : 'Offer!'}
                  </span>
                )}
                <button
                  onClick={() => handleLeaveWaitlist(w.id)}
                  className="ml-1 w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-red-500"
                  title={language === 'de' ? 'Von Warteliste entfernen' : 'Leave waitlist'}
                  data-testid={`leave-waitlist-${w.id}`}
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Date Navigation */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Mini Calendar */}
        <div className="lg:w-80">
          <div className="card-static p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={dateLocale}
              className="rounded-lg"
              disabled={(date) => {
                const today = new Date(new Date().setHours(0,0,0,0));
                if (date < today) return true;
                if (maxBookingDate && date > maxBookingDate) return true;
                return false;
              }}
            />
          </div>
        </div>

        {/* Time Grid — visible on all screens */}
        <div className="flex-1">
          {/* Date Header */}
          <div className="flex items-center justify-between mb-4 bg-white border border-slate-200 p-4 rounded-lg">
            <button 
              onClick={() => navigateDate(-1)}
              disabled={isAtToday}
              className={`p-2 rounded-lg transition-colors ${isAtToday ? 'opacity-30 cursor-not-allowed' : 'hover:bg-slate-100'}`}
              data-testid="prev-day"
            >
              <CaretLeft size={24} className="text-slate-900" />
            </button>
            <div className="text-center">
              <p className="text-xl sm:text-2xl font-bold text-slate-900">
                {format(selectedDate, 'EEEE', { locale: dateLocale })}
              </p>
              <p className="text-slate-500 text-sm sm:text-base">
                {format(selectedDate, 'PP', { locale: dateLocale })}
              </p>
            </div>
            <button 
              onClick={() => navigateDate(1)}
              className={`p-2 rounded-lg transition-colors ${isAtMaxDate ? 'opacity-30 cursor-not-allowed' : 'hover:bg-slate-100'}`}
              data-testid="next-day"
              disabled={isAtMaxDate}
            >
              <CaretRight size={24} className="text-slate-900" />
            </button>
          </div>

          {/* Court Headers — hidden when club is closed */}
          {pricingInfo?.is_closed ? (
            <div className="py-16 text-center" data-testid="closure-empty-state">
              <div className="text-6xl mb-4">🚫</div>
              <p className="text-slate-900 font-bold text-xl mb-2">
                {language === 'de' ? 'Keine Buchungen möglich' : 'No bookings available'}
              </p>
              <p className="text-slate-500 max-w-md mx-auto">
                {pricingInfo.closure_note || (language === 'de'
                  ? 'Der Club ist an diesem Tag geschlossen. Bitte wähle ein anderes Datum.'
                  : 'The club is closed on this day. Please pick another date.')}
              </p>
            </div>
          ) : (
          <div>
            {/* Duration filter — pick the length first, then only start times
                where that duration is playable on at least one court are
                highlighted below. Same UX as Open-Matches for consistency. */}
            <div className="mb-5 rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-3 sm:p-4" data-testid="duration-filter-strip">
              <p className="text-sm sm:text-base font-black text-slate-900 mb-2">
                {language === 'de' ? '1. Wie lange möchtest du spielen?' : '1. How long do you want to play?'}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[60, 90, 120].map(d => (
                  <button
                    key={d}
                    onClick={() => setFilterDuration(d)}
                    className={`py-3 sm:py-3.5 rounded-xl text-base sm:text-lg font-black border-2 transition-colors ${
                      filterDuration === d
                        ? 'bg-emerald-500 border-emerald-600 text-white shadow-md'
                        : 'bg-white border-slate-300 text-slate-700 hover:border-emerald-400 hover:text-emerald-700'
                    }`}
                    data-testid={`duration-filter-${d}`}
                  >
                    {d} <span className="text-xs sm:text-sm font-bold">min</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-600 mt-2">
                {language === 'de' ? '2. Startzeit wählen — die Zeiten zeigen, wo diese Dauer noch frei ist.' : '2. Pick a start time — times show where this length is still free.'}
              </p>
            </div>
            {/* Time-Pill Grid — the client sees the whole day at a glance.
                Green pill = at least one court free · Grey pill = fully booked
                · Amber pill = evening premium · Faded = past. Tapping opens
                the court picker so the client never scrolls a huge table. */}
            {loading ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-2">
                {[...Array(14)].map((_, i) => (
                  <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-xl" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-2" data-testid="time-pill-grid">
                {timeSlots.map(time => {
                  const past = isPastSlot(time);
                  // A court counts as "available" only when it's free AND at
                  // least one duration option (60/90/120) fits without leaving
                  // an unusable <60min orphan gap — otherwise the pill would
                  // open a court picker that dead-ends in an empty duration
                  // dropdown, which users read as "the app is broken".
                  const availableCourts = past
                    ? []
                    : courts.filter(c => !isSlotBooked(c.id, time) && hasBookableDuration(c.id, time, filterDuration));
                  const availableCount = availableCourts.length;
                  const totalCount = courts.length;
                  const hour = parseInt(time.split(':')[0]);
                  const isEvening = !pricingInfo?.is_weekend && hour >= 17;
                  const myBooking = courts
                    .map(c => getSlotBooking(c.id, time))
                    .find(b => b && b.user_id === user.id && b.start_time === time);
                  const disabledRaw = past || availableCount === 0;
                  // Free with a SHORTER length? Offer it instead of showing "Belegt".
                  const shorter = (!past && availableCount === 0)
                    ? [90, 60].filter(d => d < filterDuration).find(d => courts.some(c => !isSlotBooked(c.id, time) && hasBookableDuration(c.id, time, d)))
                    : null;
                  const disabled = disabledRaw && !shorter;

                  let pillClasses;
                  if (past) {
                    pillClasses = 'bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed';
                  } else if (myBooking) {
                    pillClasses = 'bg-emerald-500 border-emerald-600 text-white cursor-default';
                  } else if (shorter) {
                    pillClasses = 'bg-white border-dashed border-emerald-400 text-emerald-800 hover:bg-emerald-50 active:scale-95 cursor-pointer';
                  } else if (availableCount === 0) {
                    pillClasses = 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed';
                  } else if (isEvening) {
                    pillClasses = 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 hover:border-amber-500 active:scale-95 cursor-pointer';
                  } else {
                    pillClasses = 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100 hover:border-emerald-500 active:scale-95 cursor-pointer';
                  }

                  return (
                    <button
                      key={time}
                      disabled={disabled && !myBooking}
                      onClick={() => {
                        if (shorter) { setFilterDuration(shorter); setTimePickerFor(time); return; }
                        if (!disabled) setTimePickerFor(time);
                      }}
                      className={`relative border-2 rounded-xl px-2 py-3 transition-all font-bold text-center ${pillClasses}`}
                      data-testid={`time-pill-${time}`}
                    >
                      <div className="text-lg sm:text-xl leading-none tabular-nums">{time}</div>
                      <div className="text-[10px] font-semibold mt-1 opacity-80 whitespace-nowrap">
                        {past
                          ? (language === 'de' ? 'Vergangen' : 'Past')
                          : myBooking
                          ? (language === 'de' ? 'Deine Buchung' : 'Your Booking')
                          : shorter
                          ? (language === 'de' ? `nur ${shorter} min` : `${shorter} min only`)
                          : availableCount === 0
                          ? (language === 'de' ? 'Belegt' : 'Booked')
                          : `${availableCount}/${totalCount} ${language === 'de' ? 'frei' : 'free'}`}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-4 sm:gap-6 mt-4 text-xs sm:text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-50 border-2 border-emerald-300 rounded-lg" />
              <span className="text-slate-500">{language === 'de' ? 'Verfügbar' : 'Available'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-50 border-2 border-amber-300 rounded-lg" />
              <span className="text-slate-500">{language === 'de' ? 'Abend' : 'Evening'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-slate-100 border-2 border-slate-200 rounded-lg" />
              <span className="text-slate-500">{language === 'de' ? 'Belegt' : 'Booked'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-500 border-2 border-emerald-600 rounded-lg" />
              <span className="text-slate-500">{language === 'de' ? 'Deine Buchung' : 'Your Booking'}</span>
            </div>
          </div>

          {/* Fully-booked slots — quick join Waitlist (desktop) */}
          {(() => {
            const fullyBookedTimes = timeSlots.filter(time => {
              if (isPastSlot(time)) return false;
              if (!courts.length) return false;
              return courts.every(c => isSlotBooked(c.id, time));
            });
            if (fullyBookedTimes.length === 0) return null;
            return (
              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="fully-booked-section">
                <div className="flex items-center gap-2 mb-3">
                  <Bell size={16} weight="fill" className="text-amber-600" />
                  <h3 className="text-sm font-bold text-amber-800">
                    {language === 'de' ? 'Vollständig belegt — Warteliste verfügbar' : 'Fully booked — waitlist available'}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {fullyBookedTimes.map(time => {
                    const onList = isOnWaitlist(selectedDate, time);
                    return (
                      <button
                        key={time}
                        onClick={() => {
                          if (onList) {
                            const entry = myWaitlist.find(w => w.start_time === time && w.court_type === courtType && w.date === format(selectedDate, 'yyyy-MM-dd'));
                            if (entry) handleLeaveWaitlist(entry.id);
                          } else {
                            handleJoinWaitlist(time);
                          }
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                          onList
                            ? 'bg-emerald-100 border-emerald-300 text-emerald-700 hover:bg-emerald-200'
                            : 'bg-white border-amber-300 text-amber-700 hover:bg-amber-100'
                        }`}
                        data-testid={`desktop-waitlist-toggle-${time}`}
                      >
                        <Bell size={12} weight={onList ? 'fill' : 'duotone'} />
                        {time}
                        <span className="opacity-70">
                          {onList
                            ? (language === 'de' ? '· auf Liste' : '· on list')
                            : (language === 'de' ? '+ Warteliste' : '+ Waitlist')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

      </div>

      {/* Court Picker Dialog — opens when a customer taps a time pill. Lists
          only the courts still free at that time so the client can pick and
          go straight into the booking modal. */}
      <Dialog open={!!timePickerFor} onOpenChange={(open) => !open && setTimePickerFor(null)}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="court-picker-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {language === 'de' ? 'Platz wählen' : 'Choose court'} · {timePickerFor}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {language === 'de'
                ? 'Wähle einen freien Platz — Dauer und Bezahlung folgen im nächsten Schritt.'
                : 'Pick a free court — duration and payment come next.'}
            </DialogDescription>
          </DialogHeader>

          {timePickerFor && (() => {
            // Same guard as the pill grid: only offer courts that have at
            // least one bookable duration from this start time.
            const freeCourts = courts.filter(c => !isSlotBooked(c.id, timePickerFor) && hasBookableDuration(c.id, timePickerFor, filterDuration));
            if (freeCourts.length === 0) {
              return (
                <p className="text-sm text-slate-500 py-4 text-center">
                  {language === 'de' ? 'Keine Plätze mehr frei.' : 'No courts free.'}
                </p>
              );
            }
            return (
              <div className="grid grid-cols-2 gap-2 mt-2">
                {freeCourts.map(court => (
                  <button
                    key={court.id}
                    onClick={() => {
                      const t = timePickerFor;
                      setTimePickerFor(null);
                      handleSlotClick(court, t);
                    }}
                    className="bg-emerald-50 border-2 border-emerald-200 rounded-xl px-4 py-4 text-left hover:bg-emerald-100 hover:border-emerald-400 active:scale-95 transition-all"
                    data-testid={`picker-court-${court.id}`}
                  >
                    <div className="font-bold text-emerald-800">{court.name}</div>
                    {court.description && (
                      <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{court.description}</div>
                    )}
                    <div className="text-[10px] font-semibold text-emerald-600 mt-1 uppercase tracking-wider">
                      {court.is_indoor === false
                        ? (language === 'de' ? 'Aussen' : 'Outdoor')
                        : (language === 'de' ? 'Indoor' : 'Indoor')}
                    </div>
                  </button>
                ))}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md max-h-[calc(100vh-9rem)] overflow-y-auto top-[2%] translate-y-0 sm:top-[50%] sm:translate-y-[-50%]" data-testid="booking-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {language === 'de' ? 'Platz buchen' : 'Book Court'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {language === 'de' 
                ? 'Bestätigen Sie Ihre Buchungsdetails'
                : 'Confirm your booking details'}
            </DialogDescription>
          </DialogHeader>

          {selectedSlot && (
            <div className="space-y-6 mt-4">
              {/* Booking Details */}
              <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">{t('courts')}</span>
                  <span className="text-slate-900 font-bold">{selectedSlot.court.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">{language === 'de' ? 'Datum' : 'Date'}</span>
                  <span className="text-slate-900">{format(selectedSlot.date, 'PP', { locale: dateLocale })}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">{language === 'de' ? 'Zeit' : 'Time'}</span>
                  <span className="text-slate-900">{selectedSlot.time}</span>
                </div>
              </div>

              {/* Duration Select */}
              <div className="space-y-2">
                <label className="label-text">{t('duration')}</label>
                <Select value={bookingDuration} onValueChange={setBookingDuration}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-50 border-slate-200">
                    {(() => {
                      return fittingDurations(selectedSlot?.court?.id, selectedSlot?.time).map(d => (
                        <SelectItem key={d} value={String(d)}>{d} {t('minutes')}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>

              {/* Split-payment toggle — padel only AND only when paying online.
                  For cash/credits/vouchers, the booker always pays the full court
                  (`payScope='all'` is auto-forced in the payment-method button below). */}
              {courtType === 'padel' && paymentMethod === 'online' && (
                <div className="space-y-2" data-testid="pay-scope-block">
                  <label className="label-text">{language === 'de' ? 'Was möchtest du bezahlen?' : 'What do you want to pay?'}</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setPayScope('self')}
                      className={`p-3 border rounded-xl transition-all text-left ${
                        payScope === 'self'
                          ? 'border-emerald-500 bg-emerald-500/10 shadow-sm'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid="pay-scope-self"
                    >
                      <div className="text-sm font-bold text-slate-900">{language === 'de' ? 'Nur mein Anteil' : 'Only my share'}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{language === 'de' ? '1/4 vom Platz · Rest zahlen die anderen' : '1/4 of court · rest paid by others'}</div>
                    </button>
                    <button
                      onClick={() => setPayScope('all')}
                      className={`p-3 border rounded-xl transition-all text-left ${
                        payScope === 'all'
                          ? 'border-emerald-500 bg-emerald-500/10 shadow-sm'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid="pay-scope-all"
                    >
                      <div className="text-sm font-bold text-slate-900">{language === 'de' ? 'Ganzer Platz' : 'Whole court'}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{language === 'de' ? 'Ich zahle für alle 4' : "I'll pay for all 4"}</div>
                    </button>
                  </div>
                </div>
              )}

              {/* Price */}
              <div className="py-4 border-t border-b border-slate-200 space-y-1.5">
                {courtType === 'padel' && paymentMethod === 'online' && payScope === 'self' && (
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{language === 'de' ? 'Platzpreis total' : 'Full court price'}</span>
                    <span>{calculateFullPrice().toFixed(0)} CHF</span>
                  </div>
                )}
                {calculateMemberDiscount() > 0 && (
                  <div className="flex items-center justify-between text-xs text-emerald-600 font-bold" data-testid="member-discount-line">
                    <span>{language === 'de' ? 'Member-Rabatt (20% auf deinen 1/4 Anteil)' : 'Member discount (20% on your 1/4 share)'}</span>
                    <span>−{calculateMemberDiscount().toFixed(2)} CHF</span>
                  </div>
                )}
                {/* Pay-at-club members: full price shown, discount applied at till */}
                {paymentMethod === 'cash' && calculatePotentialMemberDiscount() > 0 && (
                  <div className="flex items-start gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-2" data-testid="member-discount-at-club-note">
                    <span className="text-emerald-600 font-bold text-sm leading-none pt-0.5">%</span>
                    <span className="text-emerald-700 leading-snug">
                      {language === 'de'
                        ? <>Als Mitglied: <b>−{calculatePotentialMemberDiscount().toFixed(2)} CHF</b> werden vor Ort an der Kasse abgezogen.</>
                        : <>Member perk: <b>−{calculatePotentialMemberDiscount().toFixed(2)} CHF</b> will be deducted at the club till.</>}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-lg text-slate-900 font-semibold">
                    {paymentMethod === 'cash'
                      ? (language === 'de' ? 'Vor Ort zahlbar' : 'Payable at club')
                      : paymentMethod === 'credits'
                        ? (language === 'de' ? 'Vom Guthaben' : 'From credits')
                        : courtType === 'padel'
                          ? (payScope === 'self'
                              ? (language === 'de' ? 'Du zahlst jetzt' : 'You pay now')
                              : (language === 'de' ? 'Du zahlst jetzt (ganzer Platz)' : 'You pay now (whole court)'))
                          : t('price')}
                  </span>
                  <span className="text-2xl font-black text-emerald-600" data-testid="booking-total-amount">
                    {paymentMethod === 'golf_yearly'
                      ? (language === 'de' ? 'Inklusive · Golf Jahres-Abo' : 'Included · Golf yearly abo')
                      : `${calculatePrice().toFixed(2)} CHF`}
                  </span>
                </div>
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <label className="label-text">{language === 'de' ? 'Zahlungsart' : 'Payment Method'}</label>

                {/* Golf Yearly Abo — only for Golf slots when the user has an active membership */}
                {courtType === 'golf' && myGolfYearlyAbo && (
                  <button
                    onClick={() => { setPaymentMethod('golf_yearly'); setPayScope('all'); }}
                    className={`w-full flex items-center justify-between p-3.5 border rounded-xl transition-all ${
                      paymentMethod === 'golf_yearly'
                        ? 'border-amber-500 bg-amber-500/10 shadow-sm'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="pay-golf-yearly"
                  >
                    <div className="flex items-center gap-3">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={paymentMethod === 'golf_yearly' ? '#d97706' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2l3 7h7l-6 4 3 7-7-5-7 5 3-7-6-4h7z" />
                      </svg>
                      <div className="text-left">
                        <span className="text-sm font-medium text-slate-900 block">
                          {language === 'de' ? 'Golf Jahres-Abo verwenden' : 'Use Golf yearly membership'}
                        </span>
                        <span className="text-xs text-slate-400">
                          {myGolfYearlyAbo.type === 'golf_yearly_group'
                            ? (language === 'de' ? 'Mit Gästen' : 'With guests')
                            : 'Solo'}
                          {' · '}
                          {language === 'de' ? 'Gültig bis' : 'Valid until'}{' '}
                          {new Date(myGolfYearlyAbo.valid_until).toLocaleDateString(language === 'de' ? 'de-CH' : 'en-CH')}
                        </span>
                      </div>
                    </div>
                    {paymentMethod === 'golf_yearly' && <Check size={18} className="text-amber-600" />}
                  </button>
                )}

                {/* Pay at Club (DEFAULT — no split choice needed, always full court) */}
                <button
                  onClick={() => { setPaymentMethod('cash'); setPayScope('all'); }}
                  className={`w-full flex items-center justify-between p-3.5 border rounded-xl transition-all ${
                    paymentMethod === 'cash' 
                      ? 'border-emerald-500 bg-emerald-500/10 shadow-sm' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="pay-cash"
                >
                  <div className="flex items-center gap-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={paymentMethod === 'cash' ? '#059669' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="6" width="20" height="12" rx="2" />
                      <circle cx="12" cy="12" r="3" />
                      <path d="M6 12h.01M18 12h.01" />
                    </svg>
                    <div className="text-left">
                      <span className="text-sm font-medium text-slate-900 block">{language === 'de' ? 'Vor Ort bezahlen' : 'Pay at Club'}</span>
                      <span className="text-xs text-slate-400">{language === 'de' ? 'Bar oder Karte an der Rezeption' : 'Cash or card at reception'}</span>
                    </div>
                  </div>
                  {paymentMethod === 'cash' && <Check size={18} className="text-emerald-600" />}
                </button>

                {/* Online Payment (Datatrans: TWINT / Card / Apple Pay / Google Pay) */}
                <button
                  onClick={() => setPaymentMethod('online')}
                  className={`w-full flex items-center justify-between p-3.5 border rounded-xl transition-all ${
                    paymentMethod === 'online' 
                      ? 'border-emerald-500 bg-emerald-500/10 shadow-sm' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="pay-online"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard size={20} className={paymentMethod === 'online' ? 'text-emerald-600' : 'text-slate-400'} />
                    <div className="text-left">
                      <span className="text-sm font-medium text-slate-900 block">{language === 'de' ? 'Online bezahlen' : 'Pay Online'}</span>
                      <span className="text-xs text-slate-400">TWINT · {language === 'de' ? 'Karte' : 'Card'} · Apple Pay · Google Pay</span>
                    </div>
                  </div>
                  {paymentMethod === 'online' && <Check size={18} className="text-emerald-600" />}
                </button>

                {/* Use Credits */}
                <button
                  onClick={() => { setPaymentMethod('credits'); setPayScope('all'); }}
                  disabled={user.credits_balance < calculatePrice()}
                  className={`w-full flex items-center justify-between p-3.5 border rounded-xl transition-all ${
                    paymentMethod === 'credits' 
                      ? 'border-emerald-500 bg-emerald-500/10 shadow-sm' 
                      : user.credits_balance >= calculatePrice()
                        ? 'border-slate-200 hover:border-slate-300'
                        : 'border-slate-200 opacity-50 cursor-not-allowed'
                  }`}
                  data-testid="pay-credits"
                >
                  <div className="flex items-center gap-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={paymentMethod === 'credits' ? '#059669' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v12M8 10h8M8 14h8" />
                    </svg>
                    <div className="text-left">
                      <span className="text-sm font-medium text-slate-900 block">{language === 'de' ? 'Guthaben verwenden' : 'Use Credits'}</span>
                      <span className="text-xs text-slate-400">
                        {language === 'de' ? 'Verfügbar:' : 'Available:'} {user.credits_balance?.toFixed(0)} CHF
                      </span>
                    </div>
                  </div>
                  {paymentMethod === 'credits' && <Check size={18} className="text-emerald-600" />}
                </button>
              </div>

              {/* Cancellation policy hint */}
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 flex items-start gap-2" data-testid="booking-cancel-hint">
                <Info size={14} className="text-emerald-600 shrink-0 mt-0.5" />
                <span>
                  {language === 'de'
                    ? <>Kostenlose Stornierung bis <strong>24h</strong> vor Spielbeginn. Details in den <a href="/agb" target="_blank" rel="noopener noreferrer" className="underline">AGB</a>.</>
                    : <>Free cancellation up to <strong>24h</strong> before start. See the <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms</a>.</>}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pb-20 md:pb-0">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBookingModal(false)}
                  className="flex-1 btn-secondary"
                >
                  {t('cancel')}
                </Button>
                <Button 
                  onClick={handleBooking}
                  disabled={bookingLoading}
                  className="flex-1 btn-primary"
                  data-testid="confirm-booking"
                >
                  {bookingLoading ? t('loading') : (
                    paymentMethod === 'credits' ? t('confirm') :
                    paymentMethod === 'golf_yearly' ? (language === 'de' ? 'Mit Abo buchen' : 'Book with abo') :
                    paymentMethod === 'cash' ? (language === 'de' ? 'Buchung bestätigen' : 'Confirm Booking') :
                    t('payNow')
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingCalendar;
